import { Directive, Injector, inject, signal } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

@Directive()
export abstract class SuiFormControlBase<T> implements ControlValueAccessor {
  readonly value = signal<T | null>(null);
  readonly isDisabled = signal(false);

  private readonly _injector = inject(Injector);
  private _ngControl: NgControl | null | undefined;

  /**
   * Lazy NgControl resolver. Eager `inject(NgControl, { self: true })` creates
   * a circular dependency when the wrapper provides itself as NG_VALUE_ACCESSOR
   * via `useExisting: forwardRef(self)` and `[(ngModel)]` is used directly on
   * the wrapper element. Resolving lazily after construction avoids the cycle.
   */
  get ngControl(): NgControl | null {
    if (this._ngControl === undefined) {
      this._ngControl = this._injector.get(NgControl, null, {
        self: true,
        optional: true,
      });
    }
    return this._ngControl;
  }

  protected _onChange: (value: T | null) => void = () => {};
  protected _onTouched: () => void = () => {};

  writeValue(value: T | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected setValue(value: T | null): void {
    this.value.set(value);
    this._onChange(value);
  }

  protected markTouched(): void {
    this._onTouched();
  }
}
